import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Chip,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import Swal from 'sweetalert2';
import * as XLSX from 'exceljs';
import SCUIGenericExcelUpload from '../../../atomic/molecules/SCUIGenericExcelUpload/SCUIGenericExcelUpload';
import CutOffCommand from '../../../services/command/cutOff/CutOffCommand.services';
import { COBU } from '../cutOff.classes';
import {
    buildExcelColumnMapping,
    extractWorksheetHeaders,
    mapRowsToCutOffList,
    parseWorksheetRowsToJson,
} from '../bulkUploadTab.helpers';

interface BulkUploadTabProps {
    setIsLoading: (isLoading: boolean) => void;
    isLoading: boolean;
    cargoCompanyOptions?: { id: number; name: string }[];
    refreshGridData?: () => void;
}

export const BulkUploadTab = ({
    setIsLoading,
    isLoading,
    cargoCompanyOptions = [],
    refreshGridData,
}: BulkUploadTabProps) => {
    const { t, i18n } = useTranslation();
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [uploadResponse, setUploadResponse] = useState<any>(null);

    const selectedCompany = cargoCompanyOptions?.find((c) => c.id === Number(selectedCompanyId));

    const excelColumnKeys = [
        'departureLocationId',
        'departureLocationName',
        'arrivalLocationId',
        'arrivalLocationName',
        'distanceRangeId',
        'distanceRangeName',
        'cutoffTime',
        'commitmentDay',
        'validFromDate',
        'validToDate',
        'isActive',
        'deliveryType',
        'deliveryTypeId',
        'addressTypeId',
        'addressTypeName',
    ];

    const translationKeysByJsonKey: Record<string, string[]> = {
        cutoffTime: ['cutOffTime', 'cutoffTime'],
    };

    // Column mapping: any language header -> JSON key
    const columnMapping: Record<string, string> = useMemo(
        () => buildExcelColumnMapping(excelColumnKeys, translationKeysByJsonKey, t, i18n),
        [t, i18n]
    );

    const handleChangeCompany = (event: SelectChangeEvent) => {
        setSelectedCompanyId(event.target.value);
    };

    const onUpload = async (file: File) => {
        if (!selectedCompanyId || !selectedCompany) {
            Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('cutOff.selectCargoCompanyWarning'),
                confirmButtonText: t('general.ok'),
            });
            return;
        }

        setIsLoading(true);
        setUploadResponse(null);

        try {
            // Read the Excel file using exceljs
            const workbook = new XLSX.Workbook();
            const arrayBuffer = await file.arrayBuffer();
            await workbook.xlsx.load(arrayBuffer);

            const worksheet = workbook.worksheets[0];
            if (!worksheet) {
                throw new Error('No worksheet found');
            }

            const headers = extractWorksheetHeaders(worksheet);
            const jsonData = parseWorksheetRowsToJson(worksheet, headers, columnMapping);
            const shippingCompanyCutOffList = mapRowsToCutOffList(jsonData);

            const userName = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username ?? '';

            const requestPayload = {
                shippingCompanyId: Number(selectedCompanyId),
                shippingCompanyName: selectedCompany?.name ?? '',
                updatedBy: userName,
                shippingCompanyCutOffList,
            };

            const response = await CutOffCommand.UpsertBulkShippingCompanyCutOff(requestPayload);

            if (response) {
                setUploadResponse(response);

                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('cutOff.bulkUploadSuccess') + ', ' + t('cutOff.mismatchedDataWillBeShown'),
                    showConfirmButton: false,
                    timer: 2500,
                });

                // Refresh grid data after successful upload
                refreshGridData?.();
            }
        } catch (error: any) {
            console.error('Excel upload error:', error);
            setUploadResponse(null);
            let errorMessage = t('cutOff.bulkUploadError');

            if (error?.data?.Key) {
                errorMessage = error.data.Key;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: errorMessage,
                confirmButtonText: t('general.ok'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Parse status counts from response message
    // Format: "UpsertBulkShippingCompanyCutOff completed. Total: 12, Success: 0, Failed: 0"
    const parseStatusCountsFromMessage = (message: string) => {
        const totalMatch = /Total:\s*(\d+)/i.exec(message ?? '');
        const successMatch = /Success:\s*(\d+)/i.exec(message ?? '');
        const failedMatch = /Failed:\s*(\d+)/i.exec(message ?? '');

        return {
            total: totalMatch ? Number.parseInt(totalMatch[1], 10) : 0,
            success: successMatch ? Number.parseInt(successMatch[1], 10) : 0,
            failed: failedMatch ? Number.parseInt(failedMatch[1], 10) : 0,
        };
    };

    // Render status counts
    const renderStatusCounts = () => {
        // Try itemStatusCountInfos first (standard format)
        if (uploadResponse?.itemStatusCountInfos) {
            const { totalRecords, updatedRecords, insertedRecords, failedRecords } =
                uploadResponse.itemStatusCountInfos;
            return (
                <Box className={COBU.statusCounts} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 2, marginBottom: 2 }}>
                    <Chip
                        label={`${t('cutOff.total')}: ${totalRecords ?? 0}`}
                        sx={{ backgroundColor: '#2196f3', color: 'white' }}
                    />
                    <Chip
                        label={`${t('cutOff.updated')}: ${updatedRecords ?? 0}`}
                        sx={{ backgroundColor: '#ff9800', color: 'white' }}
                    />
                    <Chip
                        label={`${t('cutOff.inserted')}: ${insertedRecords ?? 0}`}
                        sx={{ backgroundColor: '#4caf50', color: 'white' }}
                    />
                    <Chip
                        label={`${t('cutOff.failed')}: ${failedRecords ?? 0}`}
                        sx={{ backgroundColor: '#f44336', color: 'white' }}
                    />
                </Box>
            );
        }

        // Fallback: parse from message string
        if (!uploadResponse?.message) return null;

        const { total, success, failed } = parseStatusCountsFromMessage(uploadResponse.message);

        return (
            <Box className={COBU.statusCounts} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 2, marginBottom: 2 }}>
                <Chip label={`${t('cutOff.total')}: ${total}`} sx={{ backgroundColor: '#2196f3', color: 'white' }} />
                <Chip
                    label={`${t('cutOff.success')}: ${success}`}
                    sx={{ backgroundColor: '#4caf50', color: 'white' }}
                />
                <Chip label={`${t('cutOff.failed')}: ${failed}`} sx={{ backgroundColor: '#f44336', color: 'white' }} />
            </Box>
        );
    };

    // Render failed items table from responseItems
    const renderFailedItemsTable = () => {
        const responseItems = uploadResponse?.responseItems ?? [];
        // Filter only failed items
        const failedItems = responseItems.filter((item: any) => item?.isSuccess === false);

        if (failedItems.length === 0) return null;

        return (
            <Box className={COBU.failedSection} sx={{ marginTop: 3 }}>
                <p className={COBU.failedTitle} style={{ marginBottom: 8, color: '#f44336', fontWeight: 'bold', fontSize: '16px' }}>
                    {t('cutOff.failedRecords')}
                </p>
                <TableContainer className={COBU.failedTableWrap} component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold', width: 80 }}>{t('cutOff.rowNumber')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('cutOff.recordKey')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: 100 }}>{t('cutOff.status')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{t('cutOff.message')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {failedItems.map((item: any, idx: number) => (
                                <TableRow key={idx}>
                                    <TableCell>{(item?.rowIndex ?? idx) + 1}</TableCell>
                                    <TableCell>{item?.rowKey ?? '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={t('cutOff.failed')}
                                            size="small"
                                            sx={{ backgroundColor: '#f44336', color: 'white' }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ color: '#f44336' }}>{item?.message ?? '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return (
        <Box className={COBU.root} width="100%">
            <p className={COBU.title} style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {t('cutOff.bulkUpload')}
            </p>
            <p className={COBU.description} style={{ fontSize: '12px', marginTop: '8px' }}>
                {t('cutOff.bulkUploadText')}
            </p>

            <FormControl className={COBU.fieldCargoCompany} fullWidth sx={{ width: '40%', marginBottom: '16px', marginTop: '16px' }}>
                <InputLabel id="filter-company-label">{t('cutOff.cargoCompany')}</InputLabel>
                <Select
                    className={COBU.inputSelectCargoCompany}
                    labelId="filter-company-label"
                    label={t('cutOff.cargoCompany')}
                    value={selectedCompanyId}
                    onChange={handleChangeCompany}
                >
                    {(cargoCompanyOptions ?? []).map((opt) => (
                        <MenuItem key={opt?.id} value={opt?.id}>
                            {opt?.name ?? ''}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Alert className={COBU.alertInfo} severity="info" sx={{ marginBottom: '16px' }}>
                {t('cutOff.excelInfoMessage')}
            </Alert>

            <Box className={COBU.excelUploadWrap}>
            <SCUIGenericExcelUpload
                onUpload={onUpload}
                uploadButtonText={t('cutOff.upload')}
                selectFileButtonText={t('cutOff.selectExcelFile')}
                showExample={false}
                loading={isLoading}
                style={{ width: '100%' }}
                disabled={!selectedCompanyId}
            />
            </Box>

            {/* Display status counts */}
            {renderStatusCounts()}

            {/* Display failed items table */}
            {renderFailedItemsTable()}
        </Box>
    );
};

export default BulkUploadTab;
